/**
 * Match open DFP co-traveler alerts to the COP device being tailed.
 *
 * One stranger is one follow. We resolve observers through roster aliases
 * (phone / wardriver / watch / vehicle ids), keep only recent sightings, and
 * never paint the lot by proximity. A parked cluster is sitting together,
 * not a tail.
 */

export const COTRAVELER_PULSE_COLOR = '#ff1744';
export const COTRAVELER_TINT_RING = 'rgba(255,23,68,0.18)';
export const COTRAVELER_TINT_ROW = 'rgba(255,23,68,0.35)';
/** How recent the last DFP sighting must be before COP calls it a live follow. */
export const COTRAVELER_LIVE_WITHIN_MS = 20 * 60 * 1000;
/** Below this (m/s ≈ 1.5 mph) a device is treated as parked. */
export const PARKED_SPEED_MPS = 0.67;
/** Tight cluster of our own pins — same bar DFP uses for GPS jitter. */
export const PARKED_SIGMA_M = 50;

export interface CotravelerAlertLike {
  id?: number;
  anomaly_type?: string | null;
  device_id?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  detected_at?: string | null;
  status?: string | null;
  feedback_verdict?: string | null;
  evidence?: Record<string, any> | null;
}

export interface CotravelerDeviceLike {
  device_id: string;
  entity_id?: string;
  latitude: number;
  longitude: number;
  speed?: number | null;
  phone_app_id?: string | null;
  wardriver_id?: string | null;
  watch_app_id?: string | null;
  vehicle_beacon_id?: string | null;
  drone_app_id?: string | null;
  roster_ids?: string[] | null;
}

export function isOpenDfpAlert(alert: CotravelerAlertLike): boolean {
  const status = (alert.status || '').toLowerCase();
  const feedback = (alert.feedback_verdict || '').toLowerCase();
  return status !== 'resolved' && status !== 'false_positive' && feedback !== 'false_positive';
}

export function activeCotravelerAlerts<T extends CotravelerAlertLike>(alerts: T[]): T[] {
  return alerts.filter(
    (alert) => (alert.anomaly_type || '').toLowerCase() === 'cotraveler' && isOpenDfpAlert(alert),
  );
}

export function alertLastSeenMs(alert: CotravelerAlertLike): number | null {
  const ev = alert.evidence || {};
  const raw = ev.last_seen || ev.last_seen_at || ev.last_observed || alert.detected_at;
  if (raw == null || raw === '') return null;
  const ms = new Date(String(raw).replace(' ', 'T')).getTime();
  return Number.isFinite(ms) ? ms : null;
}

export function isLiveCotravelerAlert(
  alert: CotravelerAlertLike,
  nowMs: number = Date.now(),
  withinMs: number = COTRAVELER_LIVE_WITHIN_MS,
): boolean {
  const last = alertLastSeenMs(alert);
  if (last == null) return false;
  return nowMs - last <= withinMs;
}

export function liveCotravelerAlerts<T extends CotravelerAlertLike>(
  alerts: T[],
  nowMs: number = Date.now(),
): T[] {
  return activeCotravelerAlerts(alerts).filter((alert) => isLiveCotravelerAlert(alert, nowMs));
}

export function deviceFollowLabel(device: CotravelerDeviceLike): string {
  return device.entity_id || device.device_id;
}

export function haversineMeters(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const R = 6378137;
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function pushId(out: string[], seen: Set<string>, raw: unknown): void {
  if (raw == null) return;
  const id = String(raw).trim();
  if (!id) return;
  const key = id.toLowerCase();
  if (seen.has(key)) return;
  seen.add(key);
  out.push(id);
}

export function rosterKeys(device: CotravelerDeviceLike): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  pushId(out, seen, device.device_id);
  pushId(out, seen, device.entity_id);
  pushId(out, seen, device.phone_app_id);
  pushId(out, seen, device.wardriver_id);
  pushId(out, seen, device.watch_app_id);
  pushId(out, seen, device.vehicle_beacon_id);
  pushId(out, seen, device.drone_app_id);
  if (Array.isArray(device.roster_ids)) {
    for (const item of device.roster_ids) pushId(out, seen, item);
  }
  return out;
}

function indexByRoster<T extends CotravelerDeviceLike>(devices: T[]): Map<string, T> {
  const byId = new Map<string, T>();
  for (const device of devices) {
    for (const key of rosterKeys(device)) {
      const lower = key.toLowerCase();
      if (!byId.has(lower)) byId.set(lower, device);
    }
  }
  return byId;
}

/** Operator / observer device ids stored on the alert (new detector evidence). */
export function extractObserverDeviceIds(alert: CotravelerAlertLike): string[] {
  const ev = alert.evidence || {};
  const seen = new Set<string>();
  const out: string[] = [];

  const lists = [ev.observer_device_ids, ev.operator_device_ids, ev.device_ids];
  for (const list of lists) {
    if (Array.isArray(list)) {
      for (const item of list) pushId(out, seen, item);
    } else if (typeof list === 'string') {
      for (const part of list.split(/[,;\s]+/)) pushId(out, seen, part);
    }
  }

  pushId(out, seen, ev.operator_device_id);
  pushId(out, seen, ev.observer_device_id);

  const observers = ev.observers;
  if (Array.isArray(observers)) {
    for (const row of observers) {
      if (row && typeof row === 'object') {
        pushId(out, seen, (row as { device_id?: unknown }).device_id);
      }
    }
  }

  return out;
}

function speedMps(device: CotravelerDeviceLike): number {
  const raw = device.speed;
  if (raw == null || !Number.isFinite(raw)) return 0;
  return Math.max(0, raw);
}

function positionSigmaM(devices: CotravelerDeviceLike[]): number {
  const pts = devices.filter(
    (d) => Number.isFinite(d.latitude) && Number.isFinite(d.longitude),
  );
  if (pts.length < 2) return 0;
  const meanLat = pts.reduce((s, d) => s + d.latitude, 0) / pts.length;
  const meanLon = pts.reduce((s, d) => s + d.longitude, 0) / pts.length;
  const lonM = Math.max(1, 111_320 * Math.cos((meanLat * Math.PI) / 180));
  const latVar = pts.reduce((s, d) => s + (d.latitude - meanLat) ** 2, 0) / (pts.length - 1);
  const lonVar = pts.reduce((s, d) => s + (d.longitude - meanLon) ** 2, 0) / (pts.length - 1);
  return Math.sqrt((Math.sqrt(latVar) * 111_320) ** 2 + (Math.sqrt(lonVar) * lonM) ** 2);
}

export function isParkedCluster(devices: CotravelerDeviceLike[]): boolean {
  if (devices.length === 0) return false;
  if (devices.some((d) => speedMps(d) >= PARKED_SPEED_MPS)) return false;
  if (devices.length === 1) return true;
  return positionSigmaM(devices) < PARKED_SIGMA_M;
}

function pickVictim<T extends CotravelerDeviceLike>(
  observers: T[],
  alert: CotravelerAlertLike,
): T | null {
  if (observers.length === 0) return null;
  const lat = alert.latitude;
  const lon = alert.longitude;
  if (
    observers.length === 1
    || lat == null || lon == null
    || !Number.isFinite(lat) || !Number.isFinite(lon)
  ) {
    return observers[0];
  }
  let best = observers[0];
  let bestDist = Infinity;
  for (const device of observers) {
    if (!Number.isFinite(device.latitude) || !Number.isFinite(device.longitude)) continue;
    const dist = haversineMeters(lat, lon, device.latitude, device.longitude);
    if (dist < bestDist) {
      best = device;
      bestDist = dist;
    }
  }
  return best;
}

export function devicesFollowedByCotraveler<T extends CotravelerDeviceLike>(
  devices: T[],
  alerts: CotravelerAlertLike[],
  nowMs: number = Date.now(),
): T[] {
  const live = liveCotravelerAlerts(alerts, nowMs);
  if (live.length === 0 || devices.length === 0) return [];

  const byId = indexByRoster(devices);
  const matched = new Map<string, T>();

  for (const alert of live) {
    const observers: T[] = [];
    const seen = new Set<string>();
    for (const id of extractObserverDeviceIds(alert)) {
      const device = byId.get(id.toLowerCase());
      if (device && !seen.has(device.device_id)) {
        seen.add(device.device_id);
        observers.push(device);
      }
    }
    if (observers.length === 0) continue;
    if (isParkedCluster(observers)) continue;
    const victim = pickVictim(observers, alert);
    if (victim) matched.set(victim.device_id, victim);
  }

  return Array.from(matched.values());
}

export function formatCotravelerTickerText(devices: CotravelerDeviceLike[]): string | null {
  if (devices.length === 0) return null;
  const names = devices.map(deviceFollowLabel);
  if (names.length === 1) return `1 co-traveler near ${names[0]}`;
  if (names.length === 2) return `2 co-travelers near ${names[0]}, ${names[1]}`;
  return `${names.length} co-travelers near ${names.slice(0, 2).join(', ')} +${names.length - 2}`;
}

/** Operator copy for the device-list FOLLOWED info popover. */
export const COTRAVELER_HELP = {
  title: 'FOLLOWED',
  lead: 'DFP finds a stranger that moved with our radios. COP only chips the device still being tailed — not everyone who heard the same MAC.',
  items: [
    {
      label: 'Live',
      text: 'Last sighting must be within 20 minutes. A morning hit does not follow anyone at noon. DFP can stay open for hours until someone resolves it; COP does not.',
    },
    {
      label: 'Roster',
      text: 'Observers match through device reference — phone, wardriver, watch, vehicle. We do not tag a pin just because it is nearby.',
    },
    {
      label: 'One chip',
      text: 'One stranger is one follow. The chip goes on the observer still nearest the last ping. The banner reads like “1 co-traveler near Legendaryllama”, not a scanner count.',
    },
    {
      label: 'Map',
      text: 'If those IDs do not resolve to a COP pin, the stranger can still show on the map. The lot does not light up.',
    },
    {
      label: 'Parked',
      text: 'If our devices are sitting (~0 mph, tight cluster), that is sitting together — not a tail.',
    },
  ],
} as const;
